import React, { useState, useEffect, useRef } from 'react';

interface PreGameAdProps {
  onComplete: () => void;
}

const PreGameAd = ({ onComplete }: PreGameAdProps) => {
  const [countdown, setCountdown] = useState(5);
  const [canContinue, setCanContinue] = useState(false);
  const [adLoaded, setAdLoaded] = useState(false);
  
  // Reference to the ad container
  const adContainerRef = useRef<HTMLDivElement>(null);

  // Initialize countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanContinue(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Initialize ad
  useEffect(() => {
    // Set a flag to avoid attempting multiple initializations
    let adInitialized = false;

    // Function to initialize the ad
    const initializeAd = () => {
      if (adInitialized) return;
      
      try {
        if (
          typeof window !== 'undefined' &&
          window.adsbygoogle && 
          typeof window.adsbygoogle.push === 'function'
        ) {
          window.adsbygoogle.push({});
          adInitialized = true;
          setAdLoaded(true);
          console.log('Pre-game ad pushed to adsbygoogle');
        }
      } catch (error) {
        console.error('Error initializing ad:', error);
        setAdLoaded(true);
      }
    };

    // Initialize the ad after component mounts
    initializeAd();

    // Attempt to initialize every second if not successful initially
    // This handles cases where the AdSense script loads after the component
    const adInitInterval = setInterval(() => {
      if (!adInitialized) {
        initializeAd();
      } else {
        clearInterval(adInitInterval);
      }
    }, 1000);

    return () => {
      clearInterval(adInitInterval);
    };
  }, []);

  return (
    // Fixed positioning absolute center with a proper background overlay
    <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-gray-900 z-50">
      <div className="max-w-md w-full mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white text-center">
            Today's Phoodle
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mt-1">
            A fresh puzzle awaits you
          </p>
        </div>
        
        {/* Main ad container - fixed height to prevent layout shifts */}
        <div 
          className="p-4 flex justify-center items-center bg-gray-100 dark:bg-gray-700 overflow-hidden" 
          style={{ height: '300px' }}
          ref={adContainerRef}
        >
          {!adLoaded ? (
            <div className="animate-pulse flex flex-col items-center">
              <div className="w-full h-48 bg-gray-300 dark:bg-gray-600 rounded-md"></div>
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading ad...</p>
            </div>
          ) : (
            /* pregame-ad */
            <ins 
              className="adsbygoogle"
              style={{
                display: 'block',
                width: '100%',
                height: '250px',
                overflow: 'hidden'
              }}
              data-ad-client="ca-pub-4835360542564374"
              data-ad-slot="5401135914"
              data-ad-format="auto"
              data-full-width-responsive="true"
            />
          )}
        </div>
        
        <div className="p-5 flex flex-col items-center">
          {!canContinue ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-3">
                <span className="text-green-700 dark:text-green-300 text-xl font-bold">{countdown}</span>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Please wait before continuing...
              </p>
            </div>
          ) : (
            <button
              onClick={onComplete}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors shadow-md"
            >
              Play Today's Phoodle
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreGameAd; 